import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Link,
  IconButton,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { FaFacebookF, FaWhatsapp, FaInstagram, FaEnvelope, FaPhoneAlt } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';

type ReferralFormState = {
  referrerName: string;
  referrerEmail: string;
  referrerPhone: string;
  referredName: string;
  referredEmail: string;
  referredPhone: string;
  referredAddress: string;
  notes: string;
};

const initialFormState: ReferralFormState = {
  referrerName: '',
  referrerEmail: '',
  referrerPhone: '',
  referredName: '',
  referredEmail: '',
  referredPhone: '',
  referredAddress: '',
  notes: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAVY = '#212D3B';
const GOLD = '#FCC14D';

const PublicReferralPage: React.FC = () => {
  const [form, setForm] = useState<ReferralFormState>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [duplicateMessage, setDuplicateMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const PUBLIC_URL = String(process.env.PUBLIC_URL || '').trim();
  const fallbackLogoSrc = PUBLIC_URL ? `${PUBLIC_URL}/images/brand/keystone-logo.svg` : '/images/brand/keystone-logo.svg';
  const preferredLogoSrc = PUBLIC_URL
    ? `${PUBLIC_URL}/pdf-export-assets/keystone-glazing-document-logo.png`
    : '/pdf-export-assets/keystone-glazing-document-logo.png';
  const [headerLogoSrc, setHeaderLogoSrc] = useState<string>(preferredLogoSrc);

  const endpoint = useMemo(() => {
    const base =
      String(process.env.REACT_APP_SUPABASE_URL || '').trim() ||
      'https://fggiylzoktmnjfslrpyw.supabase.co';
    return `${base.replace(/\/+$/g, '')}/functions/v1/referral-submission`;
  }, []);
  const siteHref = 'https://keystoneglazing.co.uk';

  const setField = (field: keyof ReferralFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = (): string | null => {
    if (!form.referrerName.trim()) return 'Please enter the referrer name.';
    if (!form.referrerEmail.trim()) return 'Please enter the referrer email address.';
    if (!EMAIL_REGEX.test(form.referrerEmail.trim())) return 'Please enter a valid referrer email address.';
    if (!form.referredName.trim()) return 'Please enter the referred person name.';
    if (!form.referredEmail.trim()) return 'Please enter the referred person email address.';
    if (!EMAIL_REGEX.test(form.referredEmail.trim())) return 'Please enter a valid referred email address.';
    if (!form.referredAddress.trim()) return 'Please enter the referred person address.';
    return null;
  };

  const resetNotices = () => {
    setErrorMessage(null);
    setDuplicateMessage(null);
    setSuccessMessage(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    resetNotices();

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const anonKey = String(process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (anonKey) {
        headers.apikey = anonKey;
        headers.Authorization = `Bearer ${anonKey}`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...form,
          source: 'public_referral_page',
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (response.status === 409 || payload?.duplicate) {
        setDuplicateMessage(
          String(payload?.message || 'This recipient has already been referred by this referrer.'),
        );
        return;
      }

      if (!response.ok) {
        setErrorMessage(String(payload?.error || 'Unable to submit referral right now.'));
        return;
      }

      setSuccessMessage('Referral submitted successfully. Thanks for sharing Keystone with someone you know.');
      setForm(initialFormState);
    } catch (error) {
      console.error('Failed to submit referral:', error);
      setErrorMessage('Unable to submit referral right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f7f9fc' }}>
      <Sheet sx={{ bgcolor: NAVY, color: '#fff', px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 3 }, boxShadow: 'sm' }}>
        <Box
          sx={{
            maxWidth: 980,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {[
                  { href: 'tel:01243302841', label: 'Call 01243 302841', icon: FaPhoneAlt },
                  { href: 'mailto:info@keystoneglazing.co.uk', label: 'Email', icon: FaEnvelope },
                  { href: 'https://www.facebook.com/keystoneglazing/', label: 'Facebook', icon: FaFacebookF },
                  { href: 'https://www.instagram.com/keystoneglazing/?hl=en', label: 'Instagram', icon: FaInstagram },
                  { href: 'https://api.whatsapp.com/send/?phone=447386326666', label: 'WhatsApp', icon: FaWhatsapp },
                ].map((s) => (
                  <IconButton
                    key={s.label}
                    component="a"
                    href={s.href}
                    target={s.href.startsWith('http') ? '_blank' : undefined}
                    rel={s.href.startsWith('http') ? 'noreferrer' : undefined}
                    size="sm"
                    variant="plain"
                    aria-label={s.label}
                    sx={{
                      color: GOLD,
                      bgcolor: 'transparent',
                      width: 40,
                      height: 40,
                      fontSize: 16,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255)',
                      },
                    }}
                  >
                    <IconWrapper icon={s.icon} />
                  </IconButton>
                ))}
              </Stack>

              <Stack direction="row" spacing={3} alignItems="center">
                <Box
                  component="img"
                  src={headerLogoSrc}
                  alt="Keystone"
                  loading="eager"
                  onError={() => {
                    if (headerLogoSrc !== fallbackLogoSrc) setHeaderLogoSrc(fallbackLogoSrc);
                  }}
                  sx={{
                    width: { xs: 180, sm: 300 },
                    height: { xs: 46, sm: 60 },
                    objectFit: 'contain',
                    display: 'block',
                    bgcolor: 'transparent',
                  }}
                />

                <Stack spacing={0.25} sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography level="title-md" sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Poppins', fontWeight: 900, lineHeight: 1.1 }}>
                    Refer a Friend
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Poppins' }}>
                    Share Keystone with someone who needs our services.
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>

          <Stack spacing={0.35} sx={{ textAlign: 'right' }}>
            <Chip
              variant="solid"
              sx={{
                alignSelf: 'flex-end',
                bgcolor: GOLD,
                color: '#fff',
                border: '1px solid rgba(0,0,0,0.08)',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
              }}
            >
              Referral
            </Chip>

            <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Reward: <b style={{ color: '#fff' }}>£75 Voucher Each</b>
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ maxWidth: 980, mx: 'auto', mt: 2, height: 3, borderRadius: 999, bgcolor: GOLD, opacity: 0.95 }} />
      </Sheet>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 3 } }}>
        <Stack sx={{ maxWidth: 980, mx: 'auto' }} spacing={2.25}>
          <Card
            variant="outlined"
            sx={{
              borderColor: 'rgba(252, 193, 77, 0.55)',
              bgcolor: 'rgba(252, 193, 77, 0.10)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1.5}
              sx={{ p: 2 }}
            >
              <Stack spacing={0.6}>
                <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                  Earn a reward by referring someone
                </Typography>
                <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                  Complete the details below and we will track the referral for your manual voucher process.
                </Typography>
              </Stack>
              <Stack spacing={0.25} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography level="body-sm" sx={{ color: 'rgba(33,45,59,0.75)' }}>
                  Status: <b style={{ color: NAVY }}>Open</b>
                </Typography>
              </Stack>
            </Stack>
          </Card>

          <Card variant="outlined" sx={{ maxWidth: 760 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {errorMessage && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}
              {duplicateMessage && (
                <Alert color="warning" sx={{ mb: 2 }}>
                  {duplicateMessage}
                </Alert>
              )}
              {successMessage && (
                <Alert color="success" sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <Typography level="title-md" sx={{ color: NAVY, fontWeight: 900 }}>
                    Your details
                  </Typography>
                  <FormControl required>
                    <FormLabel>Your full name</FormLabel>
                    <Input
                      value={form.referrerName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('referrerName', e.target.value)}
                      placeholder="Jane Smith"
                    />
                  </FormControl>
                  <FormControl required>
                    <FormLabel>Your email address</FormLabel>
                    <Input
                      type="email"
                      value={form.referrerEmail}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('referrerEmail', e.target.value)}
                      placeholder="jane@example.com"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Your phone number (optional)</FormLabel>
                    <Input
                      value={form.referrerPhone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('referrerPhone', e.target.value)}
                      placeholder="07700 900000"
                    />
                  </FormControl>

                  <Typography level="title-md" sx={{ mt: 1, color: NAVY, fontWeight: 900 }}>
                    Person you are referring
                  </Typography>
                  <FormControl required>
                    <FormLabel>Full name</FormLabel>
                    <Input
                      value={form.referredName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('referredName', e.target.value)}
                      placeholder="John Taylor"
                    />
                  </FormControl>
                  <FormControl required>
                    <FormLabel>Email address</FormLabel>
                    <Input
                      type="email"
                      value={form.referredEmail}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('referredEmail', e.target.value)}
                      placeholder="john@example.com"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Phone number (optional)</FormLabel>
                    <Input
                      value={form.referredPhone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('referredPhone', e.target.value)}
                      placeholder="07700 900001"
                    />
                  </FormControl>
                  <FormControl required>
                    <FormLabel>Address</FormLabel>
                    <Textarea
                      minRows={2}
                      value={form.referredAddress}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setField('referredAddress', e.target.value)}
                      placeholder="123 Example Road, London"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Notes (optional)</FormLabel>
                    <Textarea
                      minRows={2}
                      value={form.notes}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setField('notes', e.target.value)}
                      placeholder="Any extra context for the referral"
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    loading={submitting}
                    sx={{
                      bgcolor: NAVY,
                      color: '#fff',
                      fontWeight: 800,
                      '&:hover': { bgcolor: '#1a2430' },
                    }}
                  >
                    Submit referral
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>

          <Stack spacing={0.6} sx={{ textAlign: 'center', pb: 2 }}>
            <Typography level="body-sm" sx={{ color: NAVY, fontWeight: 600 }}>
              Built for Strength, Designed for Style.
            </Typography>

            <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)' }}>
              © {new Date().getFullYear()} Keystone ·{' '}
              <Link
                href={siteHref}
                target="_blank"
                rel="noreferrer"
                sx={{ color: NAVY, fontWeight: 800, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {siteHref.replace(/^https?:\/\//, '')}
              </Link>
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default PublicReferralPage;
